import type { Map } from "mapbox-gl";

const POLL_INTERVAL_MS = 120;
const GIVE_UP_AFTER_MS = 15_000;

/**
 * Runs `fn` once the style can accept addSource/addLayer, and returns a cancel function.
 *
 * `map.once("style.load", fn)` on its own is unreliable in two directions: the event never
 * fires again if the style already loaded (so a guard like `if (!map.isStyleLoaded())` can
 * wait forever), and `isStyleLoaded()` transiently reports false while sources update even
 * though the style is perfectly usable. Listening broadly *and* polling covers both.
 */
export function runWhenStyleReady(map: Map, fn: () => void): () => void {
    if (map.isStyleLoaded()) {
        fn();
        return () => { /* already ran */ };
    }

    let done = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    let giveUp: ReturnType<typeof setTimeout> | null = null;

    const cleanup = (): void => {
        if (poll !== null) {
            clearInterval(poll);
            poll = null;
        }
        if (giveUp !== null) {
            clearTimeout(giveUp);
            giveUp = null;
        }
        map.off("style.load", attempt);
        map.off("styledata", attempt);
        map.off("idle", attempt);
    };

    const run = (): void => {
        done = true;
        cleanup();
        fn();
    };

    function attempt(): void {
        if (done || !map.isStyleLoaded()) {
            return;
        }
        run();
    }

    map.on("style.load", attempt);
    map.on("styledata", attempt);
    map.on("idle", attempt);
    poll = setInterval(attempt, POLL_INTERVAL_MS);

    // Last resort: isStyleLoaded() never settled. Run anyway — callers guard with try/catch.
    giveUp = setTimeout(() => {
        if (!done) {
            run();
        }
    }, GIVE_UP_AFTER_MS);

    return () => {
        if (!done) {
            done = true;
            cleanup();
        }
    };
}
